import type {
  BrandbEntry,
  BrandEdge,
  CompanyId,
  GroupSuggestion,
  LeadCompany,
  LeadCompanyDraft,
  LeadStatus,
  PersonEdge,
} from './lead-company';
import type { LeadQuery } from './lead-company-query';

export interface LeadPage {
  readonly rows: readonly LeadCompany[];
  /** Matching rows across every page, for the pager. */
  readonly total: number;
}

/**
 * The nine fields the drawer's pipeline form may write (user ruling 2026-09-07). That ruling
 * was overridden on 2026-09-16: a company may now also be created with the fields of
 * `LeadCompanyDraft`, and brand edges may be added from the drawer (`replaceBrands`). Nothing
 * else is editable; the counters, signals and comparisons stay the backend scripts' own.
 */
export interface LeadEditPatch {
  readonly status?: LeadStatus | null;
  readonly owner?: string | null;
  readonly doNotContact?: boolean | null;
  readonly nextAction?: string | null;
  readonly nextActionDate?: string | null;
  readonly language?: string | null;
  readonly notes?: string | null;
  readonly lastContacted?: string | null;
  readonly lastContactedPerson?: string | null;
}

export type AposellJobType = 'aposell_comparison_generate' | 'sample_report_generate';

/** Counts only, never rows: `portal_report_jobs` holds thousands and this is polled. */
export interface JobCounts {
  /** Queued plus running, i.e. everything still owed a result. */
  readonly pending: number;
  readonly done: number;
  readonly failed: number;
}

export interface ComparisonJobRequest {
  readonly companyId: CompanyId;
  /** Extra per-company instructions for the prompt; omitted from params when null. */
  readonly operatorHint: string | null;
}

export interface SampleReportRequest {
  /** `portal_comparison.id`, which the job carries as `pseudobrand_id`. */
  readonly comparisonId: string;
  readonly prospectName: string;
  readonly emailTo: string;
  /** Catalogue display name; omitted from params when null. */
  readonly mapFocusBrand: string | null;
}

export type JobStatus = 'queued' | 'running' | 'succeeded' | 'failed';

export interface JobLogEntry {
  readonly jobId: string;
  readonly companyId: string | null;
  /**
   * The prospect a sample report was generated for. Report jobs are keyed to a
   * pseudobrand and a prospect rather than to a CRM company, so they carry no
   * `company_id` and this is the only name the log can show for them; comparison
   * jobs leave it null and are named from `companyId`.
   */
  readonly prospectName: string | null;
  readonly status: JobStatus;
  readonly createdAt: string;
  readonly error: string | null;
  /** One line from result_jsonb, already projected for display. */
  readonly outcome: string | null;
}

export abstract class LeadCompanyRepository {
  /** One 50-row page, filtered, sorted and counted server-side. */
  abstract page(query: LeadQuery): Promise<LeadPage>;

  /** Every row sharing one of these group keys, for the group safety rail. */
  abstract siblings(groupKeys: readonly string[]): Promise<readonly LeadCompany[]>;

  abstract update(id: CompanyId, patch: LeadEditPatch): Promise<LeadCompany>;

  /**
   * Recomputes every company's `comparisons` jsonb from live `portal_comparison` data.
   * Answers the number of companies touched.
   */
  /**
   * Replaces the whole `persons` list. Read-modify-write: the caller sends the list it read
   * plus its change, so a concurrent edit to the same company can lose one entry.
   */
  abstract replacePersons(id: CompanyId, persons: readonly PersonEdge[]): Promise<LeadCompany>;

  /**
   * Replaces the whole `brands` list, the same read-modify-write as `replacePersons`, so a
   * concurrent edit to the same company can lose one entry.
   */
  abstract replaceBrands(id: CompanyId, brands: readonly BrandEdge[]): Promise<LeadCompany>;

  /**
   * Inserts a manually sourced lead. The adapter mints the `company_id` and retries once on
   * a collision, writes `sources` as `manual`, and sends nothing else the form did not fill,
   * so status, the flags, the counters and the jsonb lists take their table defaults.
   * Answers the row as stored.
   */
  abstract create(draft: LeadCompanyDraft): Promise<LeadCompany>;

  /** brandb's identity values for a portal slug; null when brandb has no row for it. */
  abstract brandbLookup(slug: string): Promise<BrandbEntry | null>;

  /** Companies whose name matches the term and that carry a group key, for reuse in the create form. */
  abstract groupSuggestions(term: string, limit: number): Promise<readonly GroupSuggestion[]>;

  abstract refreshComparisons(): Promise<number>;

  /**
   * How the queued comparison-generation jobs are progressing. `done` and `failed` are
   * windowed, so the numbers stay about the current batch rather than growing forever.
   */
  abstract jobCounts(jobType: AposellJobType): Promise<JobCounts>;

  /** Queues one generation job per company. Answers how many rows were inserted. */
  abstract queueComparisonJobs(requests: readonly ComparisonJobRequest[]): Promise<number>;

  /** Queues one sample report. Answers the `portal_report_jobs.id` of the new row. */
  abstract queueSampleReport(request: SampleReportRequest): Promise<string>;

  abstract recentJobs(jobType: AposellJobType, limit: number): Promise<readonly JobLogEntry[]>;

  /** The newest comparison job per company, for the companies on the current page. */
  abstract latestComparisonJobs(
    companyIds: readonly string[]
  ): Promise<ReadonlyMap<string, JobLogEntry>>;

  abstract companyNames(companyIds: readonly string[]): Promise<ReadonlyMap<string, string>>;
}
